/**
 * O conteúdo de fábrica de "Ações de combate" — o catálogo, e como se aprende.
 *
 * <p><b>O catálogo é a planilha do Theo</b> ("Ações de Combate.xlsx", 22/08/2026),
 * transcrita <b>integralmente</b> — as treze colunas e o conteúdo, célula a célula,
 * inclusive a grafia original ("Flexa", "Empura"): é o dado dele, e a tela é editável.
 * A planilha tem quatro marcadores de seção, e eles viraram quatro quadros: COMUNS →
 * Ações básicas, ESPECIAIS → Ações avançadas, ANIMAIS → Ações de Criaturas — e o bloco
 * <b>AGUARDO</b>, que ficou como quadro próprio: não inventamos a classificação de nove
 * ações que a planilha deixou em aberto; o Theo distribui.
 *
 * <p>O resto — anatomia, ações iniciais, aprendizado — vem do épico-07, do GDD v3.3 e
 * das Mecânicas Místikas, revisado no chat antes de virar código. As reações (Esquivar,
 * Bloquear, esquiva base) moram na página Combate: aqui elas aparecem como as colunas
 * Oportunidade/Esquiva/Bloqueio — quais defesas cada ação permite ao alvo.
 *
 * <p>O que o time gravar por cima vence inteiro — este arquivo é o ponto de partida.
 */
import { iconeDeAcao, iconeDeAtributo, iconeDoMenu } from './iconesDeConteudo';
import type { GrupoDeQuadros, SecaoDeQuadros } from './PaginaDeQuadros';
import { col, montarTabela } from './tabelasEditaveis';
import type { Linha, Tabela } from './tabelasEditaveis';

/** As treze colunas da planilha, na ordem dela. */
const COLUNAS_DO_CATALOGO = [
  col('id', 'ID'),
  col('tipo', 'TIPO'),
  col('nome', 'NOME'),
  col('efeito', 'EFEITO', true),
  col('efeito2', 'EFEITO 2'),
  col('oportunidade', 'Oportunidade'),
  col('esquiva', 'Esquiva'),
  col('bloqueio', 'Bloqueio'),
  col('distancia', 'Distância'),
  col('pa', 'PA'),
  col('sp', 'Custo SP'),
  col('requisitos', 'Requisitos', true),
  col('descricao', 'Descrição', true),
];

const catalogo = (linhas: string[][]) => montarTabela(COLUNAS_DO_CATALOGO, linhas);

// ------------------------------------------------------------ o que é uma ação

const ANATOMIA = montarTabela(
  [col('campo', 'Campo'), col('declara', 'O que declara', true)],
  [
    ['ID, TIPO, NOME', 'Identidade e família: Ataque, Movimentação, Manobra, Posição, Cinto, Magia, e as combinações (Movimento com Ataque, Movimentação com Manobra…).'],
    ['EFEITO e EFEITO 2', 'O que a ação faz ao resolver — incluindo consumo de item ("Item -1 Flexa").'],
    ['Oportunidade · Esquiva · Bloqueio', 'Quais reações o alvo pode opor a esta ação (Sim/Não). Um disparo pode ser esquivável e não bloqueável.'],
    ['Distância', 'Em que degraus (0–4) a ação está disponível — ou "ARMA", quando a régua vem da ficha da arma equipada.'],
    ['PA e Custo SP', 'O que gasta do orçamento da rodada e da Energia.'],
    ['Requisitos', 'Arma equipada compatível, item no cinto, perícia mínima, condição ativa.'],
    ['Descrição', 'O texto que a cena narra ("Realizador* ataca…").'],
  ]
);

// ------------------------------------------------------- o catálogo (planilha)

/** COMUNS — as ações básicas, de todos. */
const BASICAS = catalogo([
  ['1', 'Ataque', 'Golpear', 'Ataque de Golpe com a arma equipada', '', 'Não', 'Sim', 'Sim', 'ARMA', '3', '0', 'Arma com ataque de Golpear equipada', 'Realizador* ataca usando a força e o peso da arma'],
  ['2', 'Ataque', 'Perfurar', 'Ataque de Estocada com a arma equipada', '', 'Não', 'Sim', 'Sim', 'ARMA', '3', '0', 'Arma com Estocar equipada', 'Realizador* ataca com a ponta de sua arma'],
  ['3', 'Ataque', 'Arremessar Arma', 'Ataque de Arremesso com Arma Equipada, Remove Arma Equipada, Arma Equipada volta no final do combate', '', 'Sim', 'Sim', 'Sim', 'ARMA', '3', '0', 'Arma de Arremessar equipada na mão', 'Realizador* Arremessa'],
  ['4', 'Ataque', 'Disparar', 'Ataque de Disparo com arma equipada', 'Item -1 Flexa', 'Sim', 'Sim', 'Não', 'ARMA', '3', '0', 'Ter Item no Cinto =1 Flexa, Distancia de ataque de disparo da arma equipada', 'Realizador* dispara'],
  ['5', 'Movimentação', 'Recuar', 'Distancia +1', '', 'Não', 'Não', 'Não', '0,1,2,3', '1', '0', '', 'Realizador* recua se afastando'],
  ['6', 'Movimentação', 'Avançar', 'Distancia -1', '', 'Não', 'Não', 'Não', '1,2,3,4', '1', '0', '', 'Realizador* avança se aproximando'],
  ['8', 'Movimentação', 'Aproximar', 'Distancia -1', '', 'Sim', 'Não', 'Não', '1', '1', '0', '', 'Realizador* Engaja em luta corporal'],
  ['13', 'Movimentação', 'Fugir', 'Inicia fugir, passa turno, na proxima rodada, se no proximo turno do inimigo se não perserguir ou você ganhar o teste de fuga e perseguição = fugiu', '', 'Sim', 'Não', 'Não', '1,2,3,4', '3', '1', '', 'Realizador* inicia uma fuga'],
  ['7', 'Manobra', 'Empurrar', 'Inicia Empurar (Teste de força contra Força, vitoria= distancia+1 derrota=nada)', '', 'Sim', 'Não', 'Não', '0', '1', '0', '', 'Realizador* Empura e sai de perto'],
  ['9', 'Posição', 'Defender', 'Ganha +3 Esquiva por uma rodada, +3 Bloqueio por uma rodada, +1 na armadura por uma rodada, passa turno', '', 'Não', 'Não', 'Não', '0, 1,2,3,4', '2', '1', '', 'Realizador* se concentra na defesa'],
  ['10', 'Posição', 'Preparar', 'Ganha +3 Pontos de Ação, Passa turno', '', 'Não', 'Não', 'Não', '0,1,2,3,4', '2', '1', '', 'Realizador* se prepara'],
  ['11', 'Cinto', 'Trocar de Arma', 'Pode trocar um equipamento da mão por outro do cinto', '', 'Sim', 'Não', 'Não', '0,1,2,3,4', '1', '1', 'Ter Arma no Cinto', 'Realizador* troca de arma'],
  ['12', 'Cinto', 'Usar Item', 'Usa item consumível que esteja no cinto', '', 'Sim', 'Sim', 'Não', '1,2,3,4', '1', '1', 'Ter Item no Cinto = Item Alquimico', 'Realizador* usa uma poção do cinto'],
]);

/** AGUARDO — o bloco que a planilha deixou sem classificação. */
const EM_AGUARDO = catalogo([
  ['', 'Ataque', 'Disparos Rápidos', '2 Ataques de Disparo com penalidade -2 nos ataques, Item -2 Flexa', '', 'Sim', 'Sim', 'Não', 'ARMA', '2', '1', 'Ter Item no Cinto =2 Flexa, Perícia Arco 2', ''],
  ['', 'Ataque', 'Estocadas Rápidas', '2 Ataques de Estocada com penalidade -2 nos ataques', '', 'Não', 'Sim', 'Sim', 'ARMA', '2', '1', 'Perícia Haste 2', ''],
  ['', 'Ataque', 'Golpe do Lenhador', 'Ataque de Golpe com penalidade no ataque -3, bonus de dano +5', '', 'Não', 'Sim', 'Sim', 'ARMA', '4', '1', 'Perícia Machado 2', ''],
  ['', 'Manobra', 'Mirar Arco', 'Bonus Ataque e dano de Disparar +3', '', 'Sim', 'Não', 'Não', '2,3,4', '1', '1', 'Perícia Arco 2', ''],
  ['', 'Manobra', 'Mirar Besta', 'Bonus Ataque e dano de Disparar +3', '', 'Sim', 'Não', 'Não', '2,3,4', '1', '1', 'Perícia Besta 2', ''],
  ['7', 'Manobra', 'Escapar', 'Inicia Escapar (Teste de AGI contra AGI, vitoria= distancia+1 derrota=nada)', '', 'Sim', 'Não', 'Não', '0', '1', '0', '', 'Realizador* Empura e sai de perto'],
  ['', 'Manobra', 'Agarrar', 'Inicia Agarrar (Teste de força contra Força, vitoria= oponente não ganha ponto de ação derrota=nada)', '', 'Sim', 'Não', 'Não', '0', '0', '1', '1 Mão livre de Equipamentos', 'Realizador* Agarra'],
  ['', 'Movimentação', 'Perseguir', 'Inicia perseguir, se inimigo fugiu, rola o teste de fuga e perseguir, se ganhar no teste, a fuga se encerrea e você ainda tem suas ações, se perder, a fuga aconteceu e o combate se sncerra', '', 'Não', 'Não', 'Não', '1,2,3,4', '2', '1', 'Oponente ter usado fugir', 'Realizador* persegue o fugitivo'],
  ['', 'Movimentação', 'Levantar', 'Encerra a Condição Caído', '', 'Sim', 'Não', 'Não', '1,2,3,4', '1', '0', 'Estar com Condição Caído', 'Realizador* Levanta'],
]);

/** ESPECIAIS — as ações avançadas. */
const AVANCADAS = catalogo([
  ['', 'Magia', 'Aura Chocante', 'Ataque mágico, oponente esquivar reduz dano pela metade (arredonda pra cima), se estiver em distancia 0 dano 3d6+3, se distancia 1 dano 2d6+2, Realizador* dano 4, perfurar 15', '', 'Não', 'Sim', 'Não', '0,1', '3', '6', '', 'O corpo do Realizador* solta uma discarga eletrica em volta de sí,'],
  ['', 'Magia', 'Misseis Mágicos', 'Ataque mágico sem esquiva, dano 2d6 penetração 3', '', 'Sim', 'Não', 'Não', '1,2,3,4', '2', '5', '', 'Realizador* lança um missel de energia que rastreia o oponente'],
  ['', 'Magia', 'Misseis Mágicos Aprimorados', 'Ataque mágico sem esquiva, dano 3d6 penetração 5', '', 'Sim', 'Não', 'Não', '1,2,3,4', '3', '8', '', 'Realizador* lança um missel de energia potente que rastreia o oponente'],
  ['', 'Magia', 'Bola de Fogo', 'Ataque mágico, oponente esquivar reduz dano pela metade (arredonda pra cima), dano 5d6+1, se estiver em distancia 1 dano no Realizador*, se distancia 2 metade do dano no Realizador*', '', 'Sim', 'Sim', 'Sim', '1,2,3,4', '5', '9', '', 'Realizador* lança uma pequena bola incandecente, que explode ao tocar o chão'],
  ['', 'Magia', 'Aparatar', 'se distancia 4 = ditancia -3, se distancia 3 = ditancia -2, se distancia 1 = distancia +3, se distancia 2 = distancia +2', '', 'Não', 'Não', 'Não', '1,2,3,4', '1', '4', '', ''],
  ['', 'Ataque com Magia', 'Golpe Explosivo', 'Ataque de Golpe com a arma equipada + 3', '', 'Não', 'Sim', 'Sim', 'ARMA', '4', '4', '', ''],
  ['', 'Ataque com Magia', 'Disparo Congelante', 'Ataque de Disparo com arma equipada, bonus dano +2d6, Item -1 Flexa', '', 'Sim', 'Sim', 'Não', 'ARMA', '3', '4', 'Ter Item no Cinto =1 Flexa,', ''],
  ['', 'Ataque', 'Disparo Rápido', 'Ataque de Disparo com arma equipada com penalidade -2, Item -1 Flexa', '', 'Sim', 'Sim', 'Sim', 'ARMA', '2', '1', 'Ter Item no Cinto =1 Flexa,', ''],
  ['', 'Ataque', 'Golpe Rápido', 'Ataque de golpe com arma equipada com penalidade -2, penalidade de dano -2', '', 'Não', 'Sim', 'Sim', 'ARMA', '2', '1', '', ''],
  ['', 'Ataque', 'Estocada Rápida', 'Ataque de Estocada com arma equipada com penalidade -2, penalidade de dano -2', '', 'Não', 'Sim', 'Sim', 'ARMA', '2', '1', '', ''],
  ['', 'Ataque', 'Disparo a Queima-roupa', 'Ataque de Disparo com arma equipada com penalidade -3, Item -1 Flexa', '', 'Não', 'Sim', 'Sim', 'ARMA', '4', '3', 'Ter Item no Cinto =1 Flexa,', ''],
  ['', 'Ataque', 'Golpear com Esquerda', 'Ataque de Golpe com equipamento da mão esquerda com penalidade -2, penalidade de dano -2', '', 'Não', 'Sim', 'Sim', 'ARMA', '2', '1', '', ''],
  ['', 'Ataque', 'Estocar com Esquerda', 'Ataque de Estocada com equipamento da mão esquerda com penalidade -2, penalidade de dano -2', '', 'Não', 'Sim', 'Sim', 'ARMA', '2', '1', '', ''],
  ['', 'Ataque', 'Rajada de Estocadas', '3 Ataques de Estocada com arma equipada com penalidade -2, penalidade de dano -2', '', 'Não', 'Sim', 'Sim', 'ARMA', '5', '2', '', ''],
  ['', 'Ataque', 'Rajada de Golpes', '3 Ataques de Golpe com arma equipada com penalidade -2, penalidade de dano -2', '', 'Não', 'Sim', 'Sim', 'ARMA', '5', '2', '', ''],
  ['', 'Ataque', 'Golpe com Pomo', 'Ataque de Pomo com arma equipada,', '', 'Não', 'Sim', 'Sim', 'ARMA', '2', '0', '', ''],
  ['', 'Movimento com Ataque', 'Investida com estocada', 'Avança até distancia de Estocada da arma, realiza Ataque de estocada com penalidade -2, bonus de dano +4', '', 'Sim', 'Sim', 'Sim', '4', '4', '1', '', 'Você corre para frente e com o impulsso, ataca o adversario com a ponta de sua arma.'],
  ['', 'Movimento com Ataque', 'Investida com golpe', 'Avança até distancia de Golpe da arma, realiza Ataque de golpe com -2 Acerto +4 Dano', '', 'Sim', 'Sim', 'Sim', '3,4', '4', '1', '', 'Você corre para frente e usa o movimento para gerrar um folpe mais forte.'],
  ['', 'Movimento com Ataque', 'Rolar e disparar', 'Distancia +1, Ataque de Disparo com arma, Item -1 Flexa', '', 'Sim', 'Sim', 'Sim', '2,3', '3', '2', 'Ter Item no Cinto =1 Flexa,', ''],
  ['', 'Movimento com Ataque', 'Disparar e Perseguir', 'Ataque de Disparo com arma equipada com penalidade -3, Item -1 Flexa, Inicia perseguir, se inimigo fugiu, rola o teste de fuga e perseguir, se ganhar no teste, a fuga se encerrea e você ainda tem suas ações, se perder, a fuga aconteceu e o combate se sncerra', '', 'Não', 'Sim', 'Sim', '0,1,2,3,4', '3', '2', 'Ter Item no Cinto =1 Flexa,', ''],
  ['', 'Movimentação', 'Evadir', 'Distancia +2', '', 'Não', 'Não', 'Não', '1,2', '1', '1', '', ''],
  ['', 'Movimentação', 'Avanço Rápido', 'Disrtancia -2', '', 'Não', 'Não', 'Não', '3,4', '1', '1', '', ''],
  ['', 'Movimento com Ataque', 'Empurrar e Arremessar', 'Inicia Empurar (Teste de Força contra Força, vitoria= distancia+1 derrota=nada), Ataque de Arremesso com Arma Equipada', '', 'Sim', 'Sim', 'Sim', '0,1', '4', '1', '', ''],
  ['', 'Movimentação com Manobra', 'Encontrão', 'Distancia se torna 0, Inicia Derrubar (Teste de Força contra Força, vitoria= oponente caido derrota= nada)', '', 'Não', 'Não', 'Não', '1', '2', '1', '', ''],
  ['', 'Movimentação com Manobra', 'Bote', 'Ditancia se torna 0, Inicia Agarrar (Teste de Força contra Força, vitoria= oponente não ganha ponto de ação derrota=nada)', '', 'Sim', 'Sim', 'Sim', '1,2', '3', '2', '', ''],
  ['', 'Manobra', 'Fintar', 'Inicia Finta (Teste de Agilidade contra Percepção, vitoria= bonus ataque de golpe ou estocada +3 derrota=nada)', '', 'Não', 'Não', 'Não', '1,2,3', '1', '1', '', ''],
  ['', 'Manobra', 'Derrubar', 'Inicia Derrubar (Teste de ataque conta esquiva, vitoria= oponente derrubado derrota=nada)', '', 'Não', 'Sim', 'Sim', '1.2.3', '2', '1', '', ''],
  ['', 'Posição', 'Ofensiva', 'Ganha +2 bonus em teste de ataque, penalidade -3 na esquiva, até começo do seu proximo turno', '', 'Não', 'Não', 'Não', '1,2,3', '1', '1', '', ''],
  ['', 'Cinto', 'Saque Rápido', 'Pode trocar um equipamento da mão por outro do cinto', '', 'Não', 'Não', 'Não', '1,2,3,4', '1', '1', 'Ter Arma no Cinto', ''],
  ['', 'Carisma', 'Intimidar', '', '', 'Não', 'Não', 'Não', '1,2,3,4', '1', '3', '', ''],
  ['', 'Carisma', 'Negociar', '', '', 'Sim', 'Não', 'Não', '', '1', '1', '', ''],
]);

/** ANIMAIS — as ações de criaturas. */
const DE_CRIATURAS = catalogo([
  ['70', 'Ataque', 'Pancada', 'Ataque de Golpear', '', 'Não', 'Sim', 'Sim', 'ARMA', '3', '0', 'Arma Ataque Natural', ''],
  ['71', 'Ataque', 'Mordida', 'Ataque de Golpear', '', 'Não', 'Sim', 'Sim', 'ARMA', '2', '0', 'Arma Mordida', ''],
  ['72', 'Ataque', 'Garrada', 'Ataque de Golpear', '', 'Não', 'Sim', 'Sim', 'ARMA', '2', '0', 'Arma Garras', ''],
  ['73', 'Ataque', 'Rabada', 'Ataque de Golpear', '', 'Não', 'Sim', 'Sim', 'ARMA', '4', '0', '', ''],
  ['74', 'Ataque', 'Chifrada', 'Ataque de Estocada', '', 'Não', 'Sim', 'Sim', 'ARMA', '2', '0', '', ''],
  ['75', 'Ataque', 'Bicada', 'Ataque de Estocada', '', 'Não', 'Sim', 'Sim', 'ARMA', '2', '0', 'Arma Bico', ''],
  ['76', 'Movimentação com Ataque', 'Bote com Garras', 'Ditancia se torna 0, Ataque de Garras, Inicia Agarrar (Teste de Força contra Força, vitoria= oponente não ganha ponto de ação derrota=nada)', '', 'Sim', 'Sim', 'Sim', '2,3,4', '4', '0', '', ''],
  ['77', 'Movimentação com Ataque', 'Investida com Chifres', '', '', 'Sim', 'Sim', 'Sim', '2,3,4', '4', '0', '', ''],
  ['78', 'Movimentação com Ataque', 'Atropelar', '', '', 'Sim', 'Sim', 'Sim', '2,3,4', '4', '0', '', ''],
  ['79', 'Ataque', 'Picada Venenosa', 'Ataque de Estocada', '', 'Não', 'Sim', 'Sim', 'ARMA', '3', '0', '', ''],
  ['80', 'Ataque', 'Mordida Venenosa', 'Ataque de Golpear', '', 'Não', 'Sim', 'Sim', 'ARMA', '3', '0', 'Arma Mordida', ''],
  ['', 'Ataque', 'Agarrar', 'Ataque de Estocada', '', 'Não', 'Sim', 'Sim', 'ARMA', '3', '0', '', ''],
]);

// ------------------------------------------------------ começando e aprendendo

const ACOES_INICIAIS = montarTabela(
  [col('regra', 'Regra'), col('valor', 'O que vale', true)],
  [
    ['As básicas são de todos', 'As ações comuns — "movimentos e ataques intuitivos a todos" — estão disponíveis desde o primeiro combate.'],
    ['As especiais, não', 'Ninguém começa sabendo uma ação especial (requires_grant): elas se aprendem — ver o quadro seguinte.'],
    ['Os gates continuam valendo', 'Mesmo uma ação disponível só aparece se a arma, a munição, a distância e os pontos baterem naquele instante.'],
    ['Perícias começam em 0', 'As básicas funcionam desde o início — sem o bônus de perícia na fórmula.'],
    ['Lista nominal', 'Nenhuma fonte lista um "kit inicial" — o critério é o de cima. A referência prática é o quadro de Ações básicas.'],
  ]
);

const APRENDENDO = montarTabela(
  [col('via', 'Via'), col('como', 'Como', true), col('custo', 'Custo', true)],
  [
    ['Nível de perícia', 'Subiu ao nível exigido, desbloqueia sozinha — a planilha já usa: "Perícia Arco 2", "Perícia Haste 2", "Perícia Machado 2".', '—'],
    ['Equipamento', 'A arma ou o item concede a ação enquanto equipado (grants_actions); desequipou, sumiu.', '—'],
    ['NPC', 'Uma ação de cena com pré-requisito narrativo: ajudou o NPC três vezes, tem a reputação, é iniciado da facção.', 'Gasta 1 turno — exceção à regra do Conversar; pode custar ouro.'],
    ['Quest', 'Recompensa ao completar ("Treinamento de Rauf" → Soco Cruzado).', 'O da quest.'],
  ]
);

// -------------------------------------------------------------------- disputa

const CONFLITOS = montarTabela(
  [
    col('assunto', 'Assunto'),
    col('fontes', 'O que as fontes dizem', true),
    col('planilha', 'O que a planilha diz', true),
    col('decisao', 'Decisão do Theo', true),
  ],
  [
    [
      'O bloco AGUARDO',
      '—',
      'Nove ações sem seção: Disparos Rápidos, Estocadas Rápidas, Golpe do Lenhador, Mirar Arco/Besta, Escapar, Agarrar, Perseguir, Levantar. Ficaram num quadro próprio.',
      '',
    ],
    [
      'Fugir',
      'Mecânicas: fugir encerra o turno e roda o teste 1d6 + AGI×2 + perícia.',
      'Custa 3 PA + 1 SP, "passa turno", e a resolução espera a rodada seguinte.',
      '',
    ],
    [
      'Agarrar',
      'Épico-07: Agarrado força distância 0 e trava movimento/fuga, só liberando Soltar-se.',
      'Vitória do teste = "oponente não ganha ponto de ação"; custa 0 PA + 1 SP e exige 1 mão livre.',
      '',
    ],
    [
      'Levantar',
      'Épico-07 esboça 2 PA.',
      '1 PA — e exige a Condição Caído.',
      '',
    ],
    [
      'Derrubar',
      'Épico-07 esboça 3 PA (aplica Caído).',
      '2 PA, teste de ataque contra esquiva.',
      '',
    ],
    [
      'Empurrar × Escapar',
      'Empurrar era a manobra anti-kite (move o alvo 1 à força).',
      'Dois irmãos com o mesmo ID 7: Empurrar (FOR contra FOR) e Escapar (AGI contra AGI), ambos distância 0, vitória = distância +1.',
      '',
    ],
    [
      'Ataque de Oportunidade',
      'Citado na simulação das Mecânicas (fugir a distância 1), sem regra formal.',
      'Virou a coluna "Oportunidade": cada ação diz se permite a reação. O custo da reação em si não consta.',
      '',
    ],
    [
      'Ações de Carisma',
      'Carisma está fora do Beta (decisão do CEO no épico-03) e não é um dos 6 atributos.',
      'Intimidar e Negociar existem como TIPO Carisma, com efeito em branco.',
      '',
    ],
    [
      'Magia no catálogo',
      'O GDD marca magia como sistema inteiro em aberto; nada de conteúdo antes do sistema.',
      'Cinco magias e dois ataques com magia já especificados, com dano e custo de SP.',
      '',
    ],
    [
      'Quais são compráveis',
      'Guarda-corpo do épico: nem toda especial pode ser comprável, senão o caminho narrativo vira loja de poder.',
      'A planilha não diz como cada avançada se aprende.',
      '',
    ],
  ]
);

// ------------------------------------------------------------ a aba Season 2+

const CARTAS = montarTabela(
  [col('regra', 'Regra'), col('valor', 'O que vale', true)],
  [
    ['A ideia', 'As ações especiais já são habilidades desbloqueadas por feito, quest ou NPC. Uma Carta pode ser a fusão: um registro de feito que ocupa espaço, desbloqueia capacidade e pontua (GDD §34).'],
    ['Status', 'Em aberto no GDD — depende do sistema de Cartas (Épico 11).'],
  ]
);

// ---------------------------------------------------------- prosa e avisos

const PROSA: Record<string, Pick<Tabela, 'texto' | 'alerta'>> = {
  anatomia: {
    texto:
      'Cada ação de combate é dado, não código — e a planilha do Theo declara exatamente os campos que o motor lê. Uma ação só entra na lista daquele instante se satisfizer todos os gates: pontos, distância, arma, munição, técnica aprendida, e não estar bloqueada por condição (Caído e Agarrado escondem movimento; Levantar só aparece com a condição ativa).',
  },
  'acoes-basicas': {
    texto:
      'O bloco COMUNS da planilha (22/08/2026), integral: as ações intuitivas a todos, disponíveis desde o primeiro combate. "ARMA" na distância quer dizer que a régua vem da ficha da arma equipada.',
  },
  'acoes-em-aguardo': {
    texto:
      'O bloco AGUARDO da planilha, integral. São nove ações que a planilha deixou sem seção — várias com requisito de perícia (Arco 2, Haste 2, Machado 2), outras com cara de básica (Escapar, Perseguir, Levantar).',
    alerta:
      'Sem classificação de fábrica, de propósito: distribuir cada uma entre básicas e avançadas é decisão do Theo — mover a linha é reescrevê-la no quadro certo e apagá-la daqui.',
  },
  'acoes-avancadas': {
    texto:
      'O bloco ESPECIAIS da planilha, integral: as ações refinadas, que se aprendem. Quase todas trocam alguma coisa — menos acerto por mais dano, menos custo por mais penalidade, mais alcance por mais energia. As de Magia e Ataque com Magia já vêm com dano e custo de SP.',
    alerta:
      'Magia é sistema em aberto no GDD ("nada de conteúdo antes do sistema") — e a planilha já especifica cinco magias. Registrado no quadro de Conflitos.',
  },
  'acoes-de-criaturas': {
    texto:
      'O bloco ANIMAIS da planilha, integral: os ataques naturais — a "arma" é o corpo (Mordida, Garras, Bico, Ataque Natural). Nenhum custa SP.',
  },
  'acoes-iniciais': {
    texto:
      'Nenhuma fonte lista um kit inicial nominal. O que está definido é o critério — e ele aponta para o quadro de Ações básicas como o repertório de partida.',
  },
  aprendendo: {
    texto:
      'Quatro vias, todas sobre a mesma infraestrutura de grant permanente (por personagem, fonte NPC, perícia, quest ou item). Ouro insuficiente não gasta turno nem ouro; ação já aprendida é recusada sem cobrar. Habilidade não se compra numa árvore de talentos: ela é ensinada por alguém que existe em algum lugar da ilha — parar um turno para ajudar o lenhador é abrir mão de produzir hoje por uma capacidade permanente.',
    alerta:
      'Guarda-corpo do épico: nem toda ação pode ser comprável com ouro, senão o aprendizado vira loja de poder e o caminho narrativo esvazia. Quais são só narrativas é decisão em aberto — ver Conflitos.',
  },
  conflitos: {
    texto:
      'Onde a planilha, os épicos e as Mecânicas divergem sobre as ações. A coluna do Theo nasce vazia de propósito: um chute pré-preenchido se lê como decisão tomada.',
  },
};

const PROSA_S2: Record<string, Pick<Tabela, 'texto' | 'alerta'>> = {
  cartas: {
    texto: 'O que fica para depois: a Carta como registro de feito que também desbloqueia uma ação.',
  },
};

// ------------------------------------------------------------------ o mapa

const QUADROS: Record<string, Tabela> = {
  anatomia: ANATOMIA,
  'acoes-basicas': BASICAS,
  'acoes-em-aguardo': EM_AGUARDO,
  'acoes-avancadas': AVANCADAS,
  'acoes-de-criaturas': DE_CRIATURAS,
  'acoes-iniciais': ACOES_INICIAIS,
  aprendendo: APRENDENDO,
  conflitos: CONFLITOS,
};

/** A aba Beta/Season 1 — a chave `acoes-de-combate` no servidor. */
export const TABELAS: Record<string, Tabela> = Object.fromEntries(
  Object.entries(QUADROS).map(([chave, quadro]) => [chave, { ...PROSA[chave], ...quadro }])
);

/** O ícone de uma linha do catálogo — resolvido pelo NOME da ação. */
const iconeDoCatalogo = (l: Linha) => iconeDeAcao(l.valores.nome ?? '');

export const SECOES: Record<string, SecaoDeQuadros> = {
  anatomia: { titulo: 'O que é uma ação', rotuloDoItem: 'campo', icone: iconeDoMenu('documento') },
  'acoes-basicas': {
    titulo: 'Ações básicas',
    rotuloDoItem: 'ação',
    icone: iconeDoMenu('espada'),
    iconeDaLinha: iconeDoCatalogo,
  },
  'acoes-em-aguardo': {
    titulo: 'Em aguardo (a classificar)',
    rotuloDoItem: 'ação',
    icone: iconeDoMenu('quadro'),
    iconeDaLinha: iconeDoCatalogo,
  },
  'acoes-avancadas': {
    titulo: 'Ações avançadas',
    rotuloDoItem: 'ação',
    icone: iconeDoMenu('rosacea'),
    iconeDaLinha: iconeDoCatalogo,
  },
  'acoes-de-criaturas': {
    titulo: 'Ações de Criaturas',
    rotuloDoItem: 'ação',
    icone: iconeDoMenu('felino'),
    iconeDaLinha: iconeDoCatalogo,
  },
  'acoes-iniciais': { titulo: 'Ações iniciais', rotuloDoItem: 'regra', icone: iconeDoMenu('sol') },
  aprendendo: {
    titulo: 'Aprendendo novas ações',
    rotuloDoItem: 'via',
    icone: iconeDeAtributo('INT'),
  },
  conflitos: { titulo: 'Conflitos e decisões', rotuloDoItem: 'conflito', destaque: true, icone: iconeDoMenu('alvo') },
};

export const GRUPOS: GrupoDeQuadros[] = [
  { id: 'anatomia', titulo: 'O que é uma ação', quadros: ['anatomia'] },
  {
    id: 'catalogo',
    titulo: 'O catálogo',
    quadros: ['acoes-basicas', 'acoes-em-aguardo', 'acoes-avancadas', 'acoes-de-criaturas'],
  },
  { id: 'comecando', titulo: 'Começando', quadros: ['acoes-iniciais'] },
  { id: 'aprendendo', titulo: 'Aprendendo', quadros: ['aprendendo'] },
  { id: 'disputa', titulo: 'Em disputa', quadros: ['conflitos'] },
];

const QUADROS_S2: Record<string, Tabela> = { cartas: CARTAS };

/** A aba Season 2+ — a chave `acoes-de-combate-season2`, gravada à parte. */
export const TABELAS_S2: Record<string, Tabela> = Object.fromEntries(
  Object.entries(QUADROS_S2).map(([chave, quadro]) => [chave, { ...PROSA_S2[chave], ...quadro }])
);

export const SECOES_S2: Record<string, SecaoDeQuadros> = {
  cartas: { titulo: 'Cartas como habilidades', rotuloDoItem: 'regra', icone: iconeDoMenu('carta') },
};

export const GRUPOS_S2: GrupoDeQuadros[] = [
  { id: 'backlog', titulo: 'O que fica para depois', quadros: ['cartas'] },
];
